// Règle CMDPS (composante mensuelle de dépassement de puissance souscrite)
// selon le domaine de tension. Miroir de QuantumCMDPSCalculator
// (backend/turpe_engine/strategies.py) : l'écran doit décrire la formule que
// le moteur applique réellement, jamais une formule écrite en dur.

// Le C68 ENEDIS écrit 'BTINF' / 'BTSUP' ; le moteur écrit 'BT_INF_36' / 'BT_SUP_36'.
export const normalizeTensionDomain = (raw) => {
  if (!raw) return null;
  const d = String(raw).toUpperCase().replace(/[\s_-]/g, '');
  if (d === 'BTINF' || d === 'BTINF36') return 'BT_INF_36';
  if (d === 'BTSUP' || d === 'BTSUP36') return 'BT_SUP_36';
  if (d === 'HTA') return 'HTA';
  if (/^HTB[123]?$/.test(d)) return d;
  return null;
};

// Priorité au domaine réellement utilisé par le calcul (analytics moteur),
// puis au C68, puis à la grille CMDPS chargée (absente en BT ≤ 36 kVA).
export const resolveTensionDomain = ({ analytics, enedisInfos } = {}) =>
  normalizeTensionDomain(analytics?.tension_domain)
  || normalizeTensionDomain(enedisInfos?.tension_domain)
  || normalizeTensionDomain(analytics?.tariff_grid?.cmdps_params?.domaine_tension)
  || null;

// Un paramètre absent de la grille reste null : on n'affiche pas de valeur inventée.
const fmtParam = (value, render) => (value == null || value === '' ? null : render(value));

export const getCmdpsRule = (domain, cmdpsParams = {}) => {
  const params = cmdpsParams || {};

  if (domain === 'BT_INF_36') {
    return {
      billed: false,
      method: 'NONE',
      formula: 'Pas de CMDPS en BT ≤ 36 kVA',
      parameter: null,
      explanation: [
        'Le TURPE ne facture aucun dépassement en BT ≤ 36 kVA.',
        'La puissance est limitée par le compteur, qui coupe au-delà de la puissance souscrite.',
        'Un max mesuré au-dessus de la PS signale un risque de coupure, pas un surcoût.',
      ],
    };
  }

  if (domain === 'BT_SUP_36') {
    return {
      billed: true,
      method: 'DURATION',
      formula: 'CMDPS = tarif horaire × heures de dépassement',
      parameter: fmtParam(params.hourly_rate_eur ?? params.hourly_rate, (v) => `${v} €/h`),
      explanation: [
        'Dépassement : P mesurée > P souscrite du cadran.',
        'Chaque pas de mesure en dépassement compte pour sa durée (pas de 10 min = 1/6 h).',
        "Le montant dépend de la durée, pas de l'ampleur du dépassement.",
        'Annuel : total des heures de dépassement × tarif horaire.',
      ],
    };
  }

  if (domain === 'HTA') {
    return {
      billed: true,
      method: 'QUADRATIC_COMPLEX',
      formula: 'CMDPS = Σ k × bᵢ × √Σ(ΔP²)',
      parameter: fmtParam(params.fixed_multiplier, (v) => `k = ${v}`),
      explanation: [
        'ΔP : dépassement de chaque pas de mesure.',
        'Calcul par mois et par cadran ; bᵢ = coefficient de puissance du cadran.',
        "Coût ∝ racine de la somme des carrés : l'ampleur et la répétition comptent.",
        'Annuel : somme des CMDPS mensuels.',
      ],
    };
  }

  if (domain && domain.startsWith('HTB')) {
    return {
      billed: true,
      method: 'QUADRATIC_SIMPLE',
      formula: 'CMDPS = α × (dépassement max du mois)²',
      parameter: fmtParam(params.alpha, (v) => `α = ${v}`),
      explanation: [
        'Seul le dépassement maximal de chaque mois est retenu.',
        'Coût ∝ carré de ce dépassement.',
        'Annuel : somme des CMDPS mensuels.',
      ],
    };
  }

  return {
    billed: null,
    method: 'UNKNOWN',
    formula: 'Domaine de tension inconnu',
    parameter: null,
    explanation: ['Impossible de décrire le calcul du dépassement sans le domaine de tension du PDL.'],
  };
};
